import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Text, TextInput } from 'react-native-paper';

import { AreaIcon, PopulationIcon, WebIcon } from '@/assets';
import { getClientErrorType } from '@/features/api/hooks/errorMiddleware';
import { useUpdateOrganizationPending } from '@/features/api/hooks/grapevine-organization/useUpdateOrganization';
import { AvatarWithMenu } from '@/features/media/components/menu/AvatarWithMenu';
import { EMPTY_AVATAR_PICKER_RESULT } from '@/features/media/types/picker';
import { DetailImageList } from '@/features/profile-setting/components/organization-edit/DetailImageList';
import { EditPreviewModal } from '@/features/profile-setting/components/organization-edit/EditPreviewModal';
import { FormActions } from '@/features/profile-setting/components/organization-edit/FormActions';
import { DESCRIPTION_MAX_LENGTH } from '@/features/profile-setting/components/organization-edit/MultilineEditorModal';
import { MultilineTeaserField } from '@/features/profile-setting/components/organization-edit/MultilineTeaserField';
import { makeOrganizationEditFormStyles } from '@/features/profile-setting/components/organization-edit/organizationEditFormStyles';
import type {
  TerritorialAuthorityEditFormValues,
  OrganizationEditFormProps
} from '@/features/profile-setting/components/organization-edit/types';
import { OrganizationDataExtensionDebugFrame } from '@/features/profile-setting/components/organization-extensions/OrganizationDataExtensionDebugFrame';
import {
  composeEditDataDefaults,
  composeEditDataPayload
} from '@/features/profile-setting/components/organization-extensions/registry';
import { getActiveExtensionSectionsForOrganization } from '@/features/profile-setting/components/organization-extensions/sectionRegistry';
import { territorialAuthorityEditSchema } from '@/features/profile-setting/schemas/organizationEdit';
import {
  areaSquareKmToPayload,
  normalizeUrlForPayload,
  populationToPayload
} from '@/features/profile-setting/utils/organizationFormValidation';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { FormTextInput } from '@/shared/components/forms/FormTextInput';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

// Reserved fact-name keys the backend recognizes for a territorial authority. Values are
// unitless — see the backend contract notes in `organizationFormValidation.ts`.
const POPULATION_FACT_NAME = 'Einwohner';
const AREA_FACT_NAME = 'Fläche';

export const TerritorialAuthorityEditForm: FC<OrganizationEditFormProps> = ({
  organization,
  onSave,
  onDelete
}) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit'
  });
  const { t: tApi } = useTranslation(LocalizationNameSpace.API);
  const isPending = useUpdateOrganizationPending();
  const styles = useMemo(() => makeOrganizationEditFormStyles(theme), [theme]);
  const iconColor = theme.colors.onSurfaceVariant;

  const [previewVisible, setPreviewVisible] = useState(false);

  const schema = useMemo(() => territorialAuthorityEditSchema(t), [t]);
  const activeExtensions = useMemo(
    () => getActiveExtensionSectionsForOrganization(organization),
    [organization]
  );

  // TerritorialAuthority facts are surfaced as fixed inputs; the freeform facts list
  // isn't editable on this form, so we mirror any existing facts through so
  // the update round-trips them.
  const existingFacts = organization.facts ?? [];
  const findFact = (name: string) =>
    existingFacts.find((fact) => fact.name === name)?.value ?? '';

  const methods = useForm<TerritorialAuthorityEditFormValues>({
    // Validate on submit; after a field has errored once, re-validate it on
    // change so the error clears as soon as the user fixes it.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
    // The screen only mounts this form once the organization is loaded, so
    // its data can seed the form directly — no reset effect needed.
    defaultValues: {
      name: organization.name ?? '',
      // Cap to the editor's max so the seeded value matches what the
      // description text area displays (it truncates at DESCRIPTION_MAX_LENGTH)
      // and passes the schema's length check.
      descriptionText: (organization.description ?? '').slice(
        0,
        DESCRIPTION_MAX_LENGTH
      ),
      population: findFact(POPULATION_FACT_NAME),
      areaSquareKm: findFact(AREA_FACT_NAME),
      url: organization.url ?? '',
      data: composeEditDataDefaults(organization, activeExtensions),
      detailImages: (organization.detailImages ?? []).map((item) => ({
        key: item.id,
        mediaItem: item
      })),
      facts: existingFacts.map((fact, index) => ({
        key: `existing-${index}-${fact.name}`,
        name: fact.name,
        value: fact.value
      })),
      logoResult: EMPTY_AVATAR_PICKER_RESULT
    }
  });
  const { control, handleSubmit, setValue, setError, clearErrors } = methods;

  const submit = useCallback(
    async (data: TerritorialAuthorityEditFormValues) => {
      // Drop any prior server-set error on `name` so a fresh submit isn't
      // blocked by stale UI (RHF doesn't clear setError'd errors on its own).
      clearErrors('name');
      // Rebuild the facts list from the fixed territorial-authority inputs, keeping any
      // other facts the org already has (that aren't the two we manage).
      const otherFacts = data.facts.filter(
        (fact) =>
          fact.name !== POPULATION_FACT_NAME && fact.name !== AREA_FACT_NAME
      );
      const facts: { name: string; value: string }[] = otherFacts.map(
        ({ name, value }) => ({ name: name.trim(), value: value.trim() })
      );
      const populationValue = populationToPayload(data.population);
      if (!isEmptyOrNull(populationValue)) {
        facts.push({ name: POPULATION_FACT_NAME, value: populationValue });
      }
      const areaValue = areaSquareKmToPayload(data.areaSquareKm);
      if (!isEmptyOrNull(areaValue)) {
        facts.push({ name: AREA_FACT_NAME, value: areaValue });
      }

      try {
        await onSave({
          name: data.name.trim(),
          url: normalizeUrlForPayload(data.url ?? ''),
          description: data.descriptionText.trim(),
          logo: data.logoResult.logo,
          detailImages: data.detailImages.map((entry) =>
            entry.temporaryMediaItemId
              ? { temporaryMediaItemId: entry.temporaryMediaItemId }
              : { mediaItemId: entry.mediaItem?.id ?? '' }
          ),
          facts,
          data: composeEditDataPayload(data.data, activeExtensions)
        });
      } catch (error) {
        // Name-in-geo-area conflict → field-level error; other errors are
        // already surfaced by the shared mutation toast. Cast to string: the
        // new type isn't in the installed generated API package yet.
        if (
          (getClientErrorType(error) as string | undefined) ===
          'ORGANIZATION_NAME_ALREADY_EXISTS_IN_GEO_AREA'
        ) {
          setError('name', {
            type: 'server',
            message: tApi('organization.errorNameAlreadyExistsInGeoArea')
          });
        }
      }
    },
    [onSave, activeExtensions]
  );

  return (
    <FormProvider {...methods}>
      <View style={styles.header}>
        <Text variant="headlineLarge" style={styles.centered}>
          {t('territorialAuthorityTitle')}
        </Text>
        <Text variant="bodyLarge" style={styles.centered}>
          {t('territorialAuthorityDescription', { region: organization.name })}
        </Text>
      </View>

      <OrganizationDataExtensionDebugFrame
        label="Organization (media)"
        color="media"
      >
        <View style={styles.avatar}>
          <AvatarWithMenu
            mediaItem={organization.logoImage}
            disabled={isPending}
            onChange={(next) =>
              setValue('logoResult', next, { shouldDirty: true })
            }
          />
        </View>
      </OrganizationDataExtensionDebugFrame>

      <OrganizationDataExtensionDebugFrame label="Organization">
        <FormTextInput
          control={control}
          name="name"
          accessibilityLabel={t('fields.name')}
          accessibilityHint={t('accessibility.fieldHint')}
          mode="outlined"
          label={t('fields.name')}
          placeholder={t('placeholders.name')}
          disabled={isPending}
        />

        <MultilineTeaserField
          name="descriptionText"
          sectionTitle={t('sections.description')}
          placeholder={t('placeholders.descriptionTerritorialAuthority')}
          editorTitle={t('descriptionEditor.title')}
          editorPlaceholder={t('descriptionEditor.placeholder')}
          maxLength={DESCRIPTION_MAX_LENGTH}
          isPending={isPending}
          styles={styles}
        />
      </OrganizationDataExtensionDebugFrame>

      {activeExtensions.map((ext) => {
        if (!ext.EditSection) {
          return <></>;
        }
        return (
          <OrganizationDataExtensionDebugFrame
            key={ext.dataKey}
            label={`OrganizationDataExtension: ${ext.dataKey}`}
            color="dataExtension"
          >
            <ext.EditSection
              namePrefix={`data.${ext.dataKey}`}
              disabled={isPending}
              styles={styles}
            />
          </OrganizationDataExtensionDebugFrame>
        );
      })}

      <OrganizationDataExtensionDebugFrame
        label="Organization (media)"
        color="media"
      >
        <DetailImageList />
      </OrganizationDataExtensionDebugFrame>

      <OrganizationDataExtensionDebugFrame label="Organization (facts)">
        <Text variant="titleMedium" style={styles.sectionTitle}>
          {t('sections.infoAndContact')}
        </Text>

        <FormTextInput
          control={control}
          name="population"
          accessibilityLabel={t('fields.population')}
          accessibilityHint={t('accessibility.fieldHint')}
          mode="outlined"
          label={t('fields.population')}
          placeholder={t('placeholders.population')}
          keyboardType="numeric"
          disabled={isPending}
          left={
            <TextInput.Icon
              icon={({ size }) => (
                <PopulationIcon fill={iconColor} width={size} height={size} />
              )}
            />
          }
        />

        <FormTextInput
          control={control}
          name="areaSquareKm"
          accessibilityLabel={t('fields.areaSquareKm')}
          accessibilityHint={t('accessibility.fieldHint')}
          mode="outlined"
          label={t('fields.areaSquareKm')}
          placeholder={t('placeholders.areaSquareKm')}
          keyboardType="decimal-pad"
          disabled={isPending}
          left={
            <TextInput.Icon
              icon={({ size }) => (
                <AreaIcon fill={iconColor} width={size} height={size} />
              )}
            />
          }
        />

        <FormTextInput
          control={control}
          name="url"
          accessibilityLabel={t('fields.url')}
          accessibilityHint={t('accessibility.fieldHint')}
          mode="outlined"
          label={t('fields.url')}
          placeholder={t('placeholders.url')}
          keyboardType="url"
          autoCapitalize="none"
          disabled={isPending}
          left={
            <TextInput.Icon
              icon={({ size }) => (
                <WebIcon fill={iconColor} width={size} height={size} />
              )}
            />
          }
        />
      </OrganizationDataExtensionDebugFrame>

      <FormActions
        isPending={isPending}
        onDelete={onDelete}
        onPreview={() => setPreviewVisible(true)}
        onSave={handleSubmit(submit)}
      />

      <EditPreviewModal
        variant="territorialAuthority"
        visible={previewVisible}
        onRequestClose={() => setPreviewVisible(false)}
        organization={organization}
      />
    </FormProvider>
  );
};
